import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import AppShell from '../../components/b2b/AppShell'
import QuizAnswerBars from '../../components/b2b/QuizAnswerBars'
import QuizDropoffFunnel from '../../components/b2b/QuizDropoffFunnel'
import { api } from '../../api/client'
import { QUIZ_REGISTRY } from '../../config/quizzes'

export default function QuizReportScreen() {
  const { slug } = useParams()
  const config = QUIZ_REGISTRY[slug]
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(Boolean(config))
  const [error, setError] = useState('')

  useEffect(() => {
    if (!config) return
    let cancelled = false
    const keys = config.questions.map((q) => q.key).join(',')
    api.get(`/api/b2b/quiz/${slug}/report?keys=${keys}`)
      .then((data) => { if (!cancelled) setReport(data) })
      .catch(() => { if (!cancelled) setError('Could not load this report. Try again.') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [config, slug])

  const blocks = useMemo(() => {
    if (!config) return []
    const order = []
    const byBlock = {}
    config.questions.forEach((q) => {
      if (q.type === 'text') return
      if (!byBlock[q.block]) {
        byBlock[q.block] = []
        order.push(q.block)
      }
      byBlock[q.block].push(q)
    })
    return order.map((block) => ({ block, questions: byBlock[block] }))
  }, [config])

  const questionByKey = useMemo(() => {
    if (!config) return {}
    return Object.fromEntries(config.questions.map((q) => [q.key, q]))
  }, [config])

  if (!config) {
    return (
      <AppShell>
        <div className="px-6 md:px-10 py-8 md:py-12 max-w-5xl mx-auto">
          <p className="text-sm text-ink-3">Quiz not found.</p>
        </div>
      </AppShell>
    )
  }

  const total = report?.total ?? 0
  const completed = report?.completed ?? 0
  const completionPct = report ? Math.round(report.completion_rate * 100) : 0
  const linked = report?.linked ?? 0

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 md:py-12 max-w-5xl mx-auto">
        <p className="text-xs font-bold uppercase tracking-wider text-ink-3">Quiz report</p>
        <h1 className="mt-1 text-2xl md:text-3xl font-bold text-ink-1">{config.title}</h1>

        {error && (
          <div className="mt-6 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}

        {loading && <p className="mt-8 text-xs text-ink-3">Loading…</p>}

        {report && (
          <>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatTile label="Respondents" value={total} />
              <StatTile label="Completed" value={completed} />
              <StatTile label="Completion rate" value={`${completionPct}%`} />
              <StatTile label="Converted to signup" value={linked} />
            </div>

            <div className="mt-6">
              <QuizDropoffFunnel funnel={report.funnel} questions={config.questions} total={total} />
            </div>

            {blocks.map(({ block, questions }) => (
              <div key={block} className="mt-10">
                <h2 className="text-xs font-bold uppercase tracking-wider text-ink-3">{block}</h2>
                <div className="mt-3 space-y-4">
                  {questions.map((q) => (
                    <QuizAnswerBars
                      key={q.key}
                      question={q}
                      counts={report.answers[q.key]?.counts ?? {}}
                      total={total}
                    />
                  ))}
                </div>
              </div>
            ))}

            <div className="mt-10">
              <h2 className="text-xs font-bold uppercase tracking-wider text-ink-3">Open-ended responses</h2>
              {report.open_text.length === 0 ? (
                <p className="mt-3 text-sm text-ink-3">No open-text answers yet.</p>
              ) : (
                <div className="mt-3 bg-white rounded-3xl shadow-card p-6 space-y-4 max-h-96 overflow-y-auto">
                  {report.open_text.map((entry, i) => (
                    <div key={i}>
                      <p className="text-[10px] uppercase tracking-wider font-bold text-ink-3">
                        {questionByKey[entry.question_key]?.question ?? entry.question_key}
                      </p>
                      <p className="text-sm text-ink-2 italic">"{entry.text}"</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </AppShell>
  )
}

function StatTile({ label, value }) {
  return (
    <div className="bg-white rounded-3xl shadow-card p-5">
      <p className="text-2xl font-bold text-ink-1">{value}</p>
      <p className="mt-1 text-[11px] uppercase tracking-wider font-bold text-ink-3">{label}</p>
    </div>
  )
}
